/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { App } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
} from 'react'
import { useRegistrationSetupContext } from './context'
import GeneralSettings from './forms/GeneralSettings'
import Payments from './forms/Payments'
import Customization from './forms/Customization'
import EmailCustomization from './forms/EmailCustomization/index'
import Integrations from './forms/Integrations/index'
import Waiver from './forms/Waiver'
import TeamCustomForm from './forms/TeamCustomForm'
import PlayerCustomForm from './forms/PlayerCustomForm'
import UnsavedChangesModal from './forms/CustomForm/UnsavedChangesModal'
import Header from './components/Header'
import SidebarNav, { MobileSidebarNav } from './components/SidebarNav'
import RegistrationSetupSkeleton from './components/RegistrationSetupSkeleton'
import { useRegistrationSetupTabs } from './hooks/useRegistrationSetupTabs'
import useWindowSize from 'src/hooks/useWindowSize'
import { useSidebarState } from 'src/hooks/useSidebarState'
import { MENU_HEIGHT } from 'src/components/css'
import { getErrorMessage } from '@sportninja/common/utils/utils'

type RegistrationSetupContentProps = {
  setIsRegistrationFormOpen: (isOpen: boolean) => void
  refetchRegistrationFn: React.MutableRefObject<
    (displayLoading?: boolean) => void
  >
  refetchRegistrationInfoFn: React.MutableRefObject<
    (displayLoading?: boolean) => void
  >
  isRegistrationFormOpen: boolean
  registrationVersion: number
  isVisible: boolean
  transitionDuration: number
}

const overlayStyle = css`
  position: fixed;
  right: 0;
  bottom: 0;
  z-index: 15;
  background-color: ${colors.SECONDARY_400};
  display: flex;
  flex-direction: column;
`

const contentWrapperStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 24px 88px 0px;

  @media (max-width: 768px) {
    max-width: none;
    padding: 0px;
  }
`

const bodyStyle = css`
  display: flex;
  flex: 1;
  overflow: hidden;
  gap: 16px;
  margin-top: 0;
`

const contentAreaStyle = css`
  flex: 1;
  overflow-y: auto;
`

const RegistrationSetupContent = ({
  setIsRegistrationFormOpen,
  refetchRegistrationFn,
  isRegistrationFormOpen,
  registrationVersion,
  isVisible,
  transitionDuration,
}: RegistrationSetupContentProps) => {
  const { width } = useWindowSize()
  const isMobile = width <= 768
  const [sidebarCollapsed] = useSidebarState()
  const sidebarWidth = isMobile ? 0 : sidebarCollapsed ? 64 : 284

  const {
    submitRegistration,
    state,
    registrationAdmin,
    isUpdated,
    refetchWaivers,
    apiWaivers,
    apiAccount,
    isAccountLoading,
    activeTab,
    setActiveTab,
    refetchRegistrationAdmin,
    isInitialLoading,
  } = useRegistrationSetupContext()

  const { message } = App.useApp()

  const isLegacyRegistration = registrationVersion === 1
  const tabs = useRegistrationSetupTabs(isLegacyRegistration)

  // Refs for custom forms to check unsaved changes
  const teamFormRef = useRef<{ hasPendingChanges: () => boolean }>(null)
  const playerFormRef = useRef<{ hasPendingChanges: () => boolean }>(null)

  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  /**
   * What the confirmed modal should do. 'submit' is the Save path (save anyway,
   * losing the open editor); 'close' is BACK/CANCEL (discard and leave).
   * Without it the modal only ever knew how to submit, so BACK could not be
   * routed through the same guard.
   */
  const [pendingAction, setPendingAction] = useState<'submit' | 'close' | null>(
    null
  )
  const [submitError, setSubmitError] = useState<string | null>(null)

  const contentAreaRef = useRef<HTMLDivElement>(null)
  const [hasScroll, setHasScroll] = useState(false)

  useLayoutEffect(() => {
    const el = contentAreaRef.current
    if (!el) {
      return
    }

    const checkScroll = () => {
      setHasScroll(el.scrollHeight > el.clientHeight)
    }

    checkScroll()

    const resizeObserver = new ResizeObserver(checkScroll)
    resizeObserver.observe(el)
    // Observe the first child so we pick up content growth/shrink
    if (el.firstElementChild) {
      resizeObserver.observe(el.firstElementChild)
    }

    return () => resizeObserver.disconnect()
    // isInitialLoading is a dependency because the content area is not mounted
    // while the skeleton is showing — without it the observer would never
    // attach, and the scroll padding would stay wrong for the modal's lifetime.
  }, [activeTab, isInitialLoading])

  useEffect(() => {
    if (isRegistrationFormOpen) {
      refetchWaivers()
    }
  }, [isRegistrationFormOpen, refetchWaivers])

  // Redirect from disabled waiver tab
  useEffect(() => {
    if (activeTab === '2' && (!apiWaivers || apiWaivers.length === 0)) {
      setActiveTab('0')
    }
  }, [activeTab, apiWaivers, setActiveTab])

  // Redirect away from the Integrations tab when the account no longer has
  // Hockey Canada enabled — the tab is hidden in that case. Hockey Canada is a
  // Canlan-only integration.
  useEffect(() => {
    // Wait for the account before judging — mid-load the flag is not yet known,
    // and redirecting now would bounce off a tab that is about to be valid.
    if (isAccountLoading) {
      return
    }
    const hockeyCanadaEnabled =
      isCanlan && apiAccount?.integrations?.hockeycanada?.is_enabled === true
    if (activeTab === '7' && !hockeyCanadaEnabled) {
      setActiveTab('0')
    }
  }, [activeTab, apiAccount, isAccountLoading, setActiveTab])

  // Redirect from disabled email tab
  useEffect(() => {
    const bothDisabled =
      !state.enableTeamRegistration && !state.enablePlayerRegistration
    if (activeTab === '6' && bothDisabled) {
      setActiveTab('0')
    }
  }, [
    activeTab,
    state.enableTeamRegistration,
    state.enablePlayerRegistration,
    setActiveTab,
  ])

  const getFormById = (id: string) => {
    switch (id) {
      case '0':
        return <GeneralSettings />
      case '1':
        return <Payments />
      case '2':
        return <Waiver />
      case '3':
        return <TeamCustomForm ref={teamFormRef} />
      case '4':
        return <PlayerCustomForm ref={playerFormRef} />

      case '5':
        return <Customization />
      case '6':
        return <EmailCustomization />
      case '7':
        return <Integrations />
      default:
        return null
    }
  }

  const executeSubmit = useCallback(async () => {
    try {
      setSubmitError(null)
      await submitRegistration()
      message.success(
        isUpdated
          ? 'Registration Updated Successfully'
          : 'Registration Created Successfully'
      )
      await refetchRegistrationFn?.current?.(isUpdated ? false : true)
      // Keep the consolidated admin payload in sync with what we just saved.
      refetchRegistrationAdmin()
      setIsRegistrationFormOpen(false)
    } catch (error: any) {
      if (error?.invalid_fields) {
        if (error.invalid_fields.currency_country_id) {
          setActiveTab('1')
        }
        if (error.invalid_fields.payment_type) {
          setActiveTab('1')
        }
        if (
          error.invalid_fields.league_processing_fee_percent ||
          error.invalid_fields.league_processing_fee_flat ||
          error.invalid_fields.payment_processing_fee_bearer
        ) {
          setActiveTab('1')
        }
        return
      }
      const errorMessage = getErrorMessage(error)
      setSubmitError(errorMessage)
    }
  }, [
    submitRegistration,
    message,
    isUpdated,
    refetchRegistrationFn,
    setIsRegistrationFormOpen,
    setActiveTab,
    refetchRegistrationAdmin,
  ])

  /**
   * True while a section or question editor is still open — the state in which
   * an unsaved option list is about to be discarded. Quiet once a question has
   * been saved through its own Save button, which already persists it.
   */
  const hasPendingChanges = useCallback(() => {
    const teamUnsaved = teamFormRef.current?.hasPendingChanges?.()
    const playerUnsaved = playerFormRef.current?.hasPendingChanges?.()

    return Boolean(teamUnsaved || playerUnsaved)
  }, [])

  const handleMainSave = useCallback(() => {
    if (hasPendingChanges()) {
      setPendingAction('submit')
      setShowUnsavedModal(true)
      return
    }

    executeSubmit()
  }, [executeSubmit, hasPendingChanges])

  /**
   * Backs both BACK and CANCEL. Leaving with an editor open threw the work away
   * silently — the guard existed but was only ever consulted on Save.
   *
   * Route-path changes and sidebar tab switches remain unguarded; only BACK and
   * CANCEL are covered here.
   */
  const handleClose = useCallback(() => {
    if (hasPendingChanges()) {
      setPendingAction('close')
      setShowUnsavedModal(true)
      return
    }

    setIsRegistrationFormOpen(false)
  }, [hasPendingChanges, setIsRegistrationFormOpen])

  const dismissUnsavedModal = useCallback(() => {
    setShowUnsavedModal(false)
    setPendingAction(null)
  }, [])

  const confirmUnsavedModal = useCallback(async () => {
    // Clear BEFORE dispatching: executeSubmit awaits the network, and a stale
    // pendingAction left sitting there would re-fire on the next confirmation.
    const action = pendingAction
    setShowUnsavedModal(false)
    setPendingAction(null)

    if (action === 'close') {
      setIsRegistrationFormOpen(false)
      return
    }

    await executeSubmit()
  }, [pendingAction, executeSubmit, setIsRegistrationFormOpen])

  const NavComponent = isMobile ? MobileSidebarNav : SidebarNav

  return (
    <div
      css={css`
        ${overlayStyle}
        top: ${isMobile ? 48 : MENU_HEIGHT}px;
        left: ${sidebarWidth}px;
        opacity: ${isVisible ? 1 : 0};
        transform: translateY(${isVisible ? '0' : '20px'});
        transition: opacity ${transitionDuration}ms ease-out,
          transform ${transitionDuration}ms ease-out, left 0.2s ease-in-out;
      `}
    >
      <div css={contentWrapperStyle}>
        <Header
          onBack={handleClose}
          onCancel={handleClose}
          onSubmit={handleMainSave}
          isLoading={state.loading}
          submitError={submitError}
          competitionName={
            registrationAdmin?.root_schedule?.name_full ||
            registrationAdmin?.root_schedule?.name ||
            undefined
          }
        />

        {isInitialLoading ? (
          <RegistrationSetupSkeleton
            stacked={isMobile}
            tabCount={tabs.length}
          />
        ) : (
          <div
            css={css`
              ${bodyStyle}
              flex-direction: ${isMobile ? 'column' : 'row'};
            `}
          >
            <NavComponent
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
            <div
              ref={contentAreaRef}
              css={css`
                ${contentAreaStyle}
                padding-right: ${hasScroll ? '8px' : '0'};
              `}
            >
              {getFormById(activeTab)}
            </div>
          </div>
        )}

        <UnsavedChangesModal
          isOpen={showUnsavedModal}
          isLoading={state.loading}
          error={null}
          onClose={dismissUnsavedModal}
          onConfirm={confirmUnsavedModal}
        />
      </div>
    </div>
  )
}

export default RegistrationSetupContent
